import React from 'react';
import { Download, ToggleLeft, ToggleRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useFilter } from '../../context/FilterContext';
import { UserRole } from '../../types';

const STATES = ['Gujarat', 'Tamil Nadu', 'Rajasthan', 'Maharashtra', 'Karnataka', 'Madhya Pradesh', 'Kerala'];
const DISTRICTS_GJ = ['Ahmedabad', 'Surat', 'Vadodara', 'Rajkot', 'Junagadh'];
const DISCOMS_GJ = ['PGVCL', 'MGVCL', 'DGVCL', 'UGVCL'];
const CIRCLES = ['Ahmedabad', 'Gandhinagar', 'Ankleshwar', 'Kheda', 'Mehsana'];
const DIVISIONS: Record<string, string[]> = {
  'Ahmedabad': ['Division East', 'Division West', 'Division Central'],
  'Gandhinagar': ['Division North', 'Division South'],
  'Ankleshwar': ['Division A', 'Division B'],
  'Kheda': ['Division I', 'Division II'],
  'Mehsana': ['Division M1', 'Division M2'],
};
const PR_THRESHOLDS = [60, 65, 70, 75, 80];

const DATE_PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This week' },
  { key: 'month', label: 'This month' },
  { key: '3months', label: 'Last 3 months' },
] as const;

export default function FilterBar() {
  const { currentRole } = useAuth();
  const {
    datePreset,
    setDatePreset,
    selectedState,
    setSelectedState,
    selectedDistrict,
    setSelectedDistrict,
    selectedDISCOM,
    setSelectedDISCOM,
    selectedCircle,
    setSelectedCircle,
    selectedFeeder,
    setSelectedFeeder,
    activeOnly,
    setActiveOnly,
    faultsOnly,
    setFaultsOnly,
    underperformersOnly,
    setUnderperformersOnly,
    statusFilter,
    setStatusFilter,
    prThreshold,
    setPRThreshold,
  } = useFilter();

  const isConsumer = currentRole === UserRole.CONSUMER;
  const isDISCOM   = currentRole === UserRole.DISCOM;
  const isVendor   = currentRole === UserRole.VENDOR;
  const isNational = currentRole === UserRole.NATIONAL;
  const isState    = currentRole === UserRole.STATE;

  const selectCls = 'text-xs border border-border rounded-input px-2.5 py-1.5 bg-white text-text-primary focus:outline-none focus:border-accent disabled:opacity-40 disabled:cursor-not-allowed';
  const toggleBtnCls = (active: boolean) =>
    `flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-input border transition-colors ${
      active
        ? 'bg-navy text-white border-navy'
        : 'bg-white text-text-secondary border-border hover:border-navy hover:text-navy'
    }`;

  /* ── Available divisions for selected circle ── */
  const availableDivisions = selectedCircle ? (DIVISIONS[selectedCircle] ?? []) : [];

  return (
    <div className="bg-white border-b border-border px-4 py-2 flex items-center gap-2 flex-wrap min-h-[44px]">

      {/* ── CONSUMER: preset pills ───────────────────────────────── */}
      {isConsumer && (
        <div className="flex items-center gap-1">
          {DATE_PRESETS.map(p => (
            <button
              key={p.key}
              onClick={() => setDatePreset(p.key)}
              className={`text-xs px-2.5 py-1.5 rounded-input border transition-colors ${
                datePreset === p.key
                  ? 'bg-consumer-green text-white border-consumer-green'
                  : 'bg-white text-text-secondary border-border hover:border-consumer-green hover:text-consumer-green'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      )}

      {/* ── NATIONAL / STATE / DISCOM / VENDOR: date range ──────── */}
      {!isConsumer && (
        <div className="flex items-center gap-1.5">
          <input type="date" defaultValue="2026-04-01" className={selectCls} />
          <span className="text-text-tertiary text-xs">–</span>
          <input type="date" defaultValue="2026-04-27" className={selectCls} />
        </div>
      )}

      {/* ── NATIONAL: State → District → DISCOM cascade ─────────── */}
      {isNational && (
        <>
          <select className={selectCls} value={selectedState} onChange={e => setSelectedState(e.target.value)}>
            <option value="">All States</option>
            {STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className={selectCls} value={selectedDistrict} onChange={e => setSelectedDistrict(e.target.value)} disabled={!selectedState}>
            <option value="">All Districts</option>
            {DISTRICTS_GJ.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select className={selectCls} value={selectedDISCOM} onChange={e => setSelectedDISCOM(e.target.value)} disabled={!selectedDistrict}>
            <option value="">All DISCOMs</option>
            {DISCOMS_GJ.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </>
      )}

      {/* ── STATE: District → DISCOM cascade (state is fixed) ───── */}
      {isState && (
        <>
          <select className={selectCls} value={selectedDistrict} onChange={e => setSelectedDistrict(e.target.value)}>
            <option value="">All Districts</option>
            {DISTRICTS_GJ.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select className={selectCls} value={selectedDISCOM} onChange={e => setSelectedDISCOM(e.target.value)} disabled={!selectedDistrict}>
            <option value="">All DISCOMs</option>
            {DISCOMS_GJ.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </>
      )}

      {/* ── DISCOM: Circle → Division → Feeder cascade ───────────── */}
      {isDISCOM && (
        <>
          <select className={selectCls} value={selectedCircle} onChange={e => setSelectedCircle(e.target.value)}>
            <option value="">All Circles</option>
            {CIRCLES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className={selectCls} value={selectedDISCOM} onChange={e => setSelectedDISCOM(e.target.value)} disabled={!selectedCircle}>
            <option value="">All Divisions</option>
            {availableDivisions.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select className={selectCls} value={selectedFeeder} onChange={e => setSelectedFeeder(e.target.value)} disabled={!selectedDISCOM}>
            <option value="">All Feeders</option>
            <option value="FDR-01">Feeder 01</option>
            <option value="FDR-02">Feeder 02</option>
            <option value="FDR-03">Feeder 03</option>
          </select>
        </>
      )}

      {/* ── VENDOR: multi-state + PR threshold ───────────────────── */}
      {isVendor && (
        <>
          <select className={selectCls} value={selectedState} onChange={e => setSelectedState(e.target.value)}>
            <option value="">All States</option>
            {STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className={selectCls} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="all">All Status</option>
            <option value="online">Online</option>
            <option value="offline">Offline</option>
            <option value="warning">Warning</option>
          </select>
          <select className={selectCls} value={prThreshold} onChange={e => setPRThreshold(Number(e.target.value))}>
            {PR_THRESHOLDS.map(t => (
              <option key={t} value={t}>PR &lt; {t}%</option>
            ))}
          </select>
        </>
      )}

      <div className="flex-1" />

      {/* ── Toggle buttons per role ───────────────────────────────── */}
      {(isNational || isState) && (
        <>
          <button onClick={() => setActiveOnly(!activeOnly)} className={toggleBtnCls(activeOnly)}>
            {activeOnly ? <ToggleRight size={13} /> : <ToggleLeft size={13} />}
            Active only
          </button>
          <button onClick={() => setFaultsOnly(!faultsOnly)} className={toggleBtnCls(faultsOnly)}>
            {faultsOnly ? <ToggleRight size={13} /> : <ToggleLeft size={13} />}
            Faults only
          </button>
        </>
      )}

      {isDISCOM && (
        <button onClick={() => setActiveOnly(!activeOnly)} className={toggleBtnCls(activeOnly)}>
          {activeOnly ? <ToggleRight size={13} /> : <ToggleLeft size={13} />}
          Active alarms only
        </button>
      )}

      {isVendor && (
        <button onClick={() => setUnderperformersOnly(!underperformersOnly)} className={toggleBtnCls(underperformersOnly)}>
          {underperformersOnly ? <ToggleRight size={13} /> : <ToggleLeft size={13} />}
          Underperformers
        </button>
      )}

      {/* ── Export button ─────────────────────────────────────────── */}
      {!isConsumer && (
        <button className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-input border border-border bg-white text-text-secondary hover:text-accent hover:border-accent transition-colors">
          <Download size={12} />
          Export CSV
        </button>
      )}
      {isConsumer && (
        <button className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-input border border-consumer-green bg-white text-consumer-green hover:bg-green-50 transition-colors">
          <Download size={12} />
          Download report
        </button>
      )}
    </div>
  );
}
